import { Link, useParams } from "react-router-dom";
import SuporteFaculdade from "./SuporteFaculdade";
import AdminPublicarDocumento from "./AdminPublicarDocumento";
import AdminPosNotas from "./AdminPosNotas";
import AdminPosMatriculas from "./AdminPosMatriculas";
import AdminPosTurmas from "./AdminPosTurmas";
import AdminPosTentativas from "./AdminPosTentativas";
import AdminPosDisciplinas from "./AdminPosDisciplinas";

// Admin › Faculdade — o despacho das três frentes que a secretaria pediu.
//
// A reunião de 31/08 pediu três painéis, e eles têm dono diferente do mesmo time:
//   · **Documentos** — a fila do balcão (`sec_fila_da_equipe`, 000130/000133).
//     Já existia desde 31/08 e é o trabalho DIÁRIO: 12.625 itens esperando.
//   · **Notas** — as 246.529 tentativas, com filtro e ordenação (000143).
//   · **Matrículas** — as 43.077, com filtros e exportação (000143).
//
// E as três que a secretaria pediu depois, entregues em 14/09 (000224):
//   · **Turmas** — qual turma recebe as matrículas novas, e a GRADE de cada
//     uma. As duas numa aba só porque partem do mesmo lugar: a lista de turmas
//     do curso. Em abas separadas, o operador escolheria a turma duas vezes.
//   · **Tentativas** — os alunos travados (esgotaram as cinco e não passaram:
//     medido, 29 de 2.804 que esgotaram) e a liberação, com motivo obrigatório.
//
// ---------------------------------------------------------------------------
// A sub-aba é SUB-ROTA, e a doutrina vale duas vezes aqui
// ---------------------------------------------------------------------------
// `/admin/faculdade/notas` em vez de `useState`, pela mesma razão do HUB DP e da
// própria Faculdade do aluno: o operador volta pelo botão do navegador,
// recarregar não o joga para a primeira aba, e — o que mais importa num painel de
// atendimento — **ele pode mandar o link da tela para um colega**. Um endereço que
// não reproduz a tela é uma tela que não se pode indicar a ninguém.
//
// `documentos` é o padrão porque é o trabalho de todo dia; notas e matrículas são
// consulta, não fila.

// E as DUAS de 16/09 (000244-000247), a pedido de Victor:
//   · **Disciplinas** — as 245 em 9 cursos. Criar já existia dentro de uma
//     turma (000228); o que faltava era CORRIGIR o nome, a carga horária e o
//     professor de uma que já existe, o que antes exigia SQL.
//   · **Docentes** — os 47 professores, com contato e nível. Eram texto solto
//     dentro de cada disciplina, e 11 deles dão aula em mais de um curso:
//     renomear era editar N linhas sem garantia de que as N ficassem iguais.
//
// As duas ficam DEPOIS de Turmas porque a ordem das abas é a do trabalho: a
// fila é diária, notas e matrículas são consulta, e o cadastro é o que se
// mexe de vez em quando — no começo de uma turma, ou quando um professor sai.

type Sub =
  | "documentos"
  | "notas"
  | "matriculas"
  | "turmas"
  | "tentativas"
  | "disciplinas"
  | "publicar";

const SUBS: Array<{ id: Sub; rotulo: string; nota: string }> = [
  { id: "documentos", rotulo: "Documentos", nota: "a fila do balcão" },
  { id: "notas", rotulo: "Notas", nota: "tentativas e avaliações" },
  { id: "matriculas", rotulo: "Matrículas", nota: "com exportação" },
  { id: "turmas", rotulo: "Turmas", nota: "corrente e grade" },
  { id: "tentativas", rotulo: "Tentativas", nota: "liberar quem travou" },
  { id: "disciplinas", rotulo: "Disciplinas", nota: "nome, carga e professor" },
  // Publicar NÃO entra no balcão: aquela é a fila do que está pendente, e
  // publicar não tem pendência — ninguém pediu (000282).
  { id: "publicar", rotulo: "Publicar", nota: "informe de rendimentos" },
];

export default function AdminFaculdade() {
  const { sub } = useParams();
  const atual: Sub = SUBS.some((s) => s.id === sub) ? (sub as Sub) : "documentos";

  return (
    <div>
      <nav
        className="mb-6 flex flex-wrap gap-2"
        role="tablist"
        aria-label="Painéis da faculdade"
      >
        {SUBS.map((s) => (
          <Link
            key={s.id}
            to={s.id === "documentos" ? "/admin/faculdade" : `/admin/faculdade/${s.id}`}
            role="tab"
            aria-selected={atual === s.id}
            data-subaba={s.id}
            className={`min-h-10 rounded-full border px-4 py-2 text-sm font-semibold transition ${
              atual === s.id
                ? "border-laranja bg-laranja text-azul"
                : "border-borda text-cinza hover:bg-chip hover:text-tinta"
            }`}
          >
            {s.rotulo}
            <span className="ml-2 hidden text-xs font-normal opacity-70 sm:inline">{s.nota}</span>
          </Link>
        ))}
      </nav>

      {atual === "documentos" && <SuporteFaculdade />}
      {atual === "notas" && <AdminPosNotas />}
      {atual === "matriculas" && <AdminPosMatriculas />}
      {atual === "turmas" && <AdminPosTurmas />}
      {atual === "tentativas" && <AdminPosTentativas />}
      {atual === "disciplinas" && <AdminPosDisciplinas />}
      {atual === "publicar" && <AdminPublicarDocumento />}
    </div>
  );
}
